import { Box, Typography } from '@mui/material';
import Payment from 'components/payment/payment.component';
import { useGiftCardPayment } from 'components/payment/payment.new.hooks';
import { makeUseLoading } from 'hooks/useLoader';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectClientName,
  selectGiftCardSelectableAmounts,
  selectMaxAmountGiftCard,
  selectMinAmountGiftCard,
} from 'redux/config/config.selectors';
import { fetchPaymentInstrumentsStartAsync } from '../../../../redux/user/user.actions';
import {
  selectIsCardLoading,
  selectPaymentInstrumentsCards,
} from '../../../../redux/user/user.selectors';
import Spinner from '../../../spinner/spinner.component';
import GiftCardAddValueForm from './gift-card-add-value-form/gift-card-add-value-form.component';

export const usePurchaseGiftcardLoading = makeUseLoading();

const GiftCardAddValue = ({
  handleClose,
  paymentInstrumentId,
  submitLabel,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cards = useSelector(selectPaymentInstrumentsCards);
  const minAmount = useSelector(selectMinAmountGiftCard);
  const maxAmount = useSelector(selectMaxAmountGiftCard);
  const [disabled, setDisabled] = useState(true);
  const defaultPayment = cards.find(({ isDefault }) => isDefault);
  const [cardId, setCardId] = useState(
    defaultPayment
      ? defaultPayment.paymentInstrumentId
      : cards && cards.length
      ? cards[0].paymentInstrumentId
      : null,
  );
  const isLoading = useSelector(selectIsCardLoading);
  const paymentLoading = usePurchaseGiftcardLoading(state => state.loading);
  const loading = useMemo(
    () => isLoading || paymentLoading,
    [isLoading, paymentLoading],
  );

  useEffect(() => {
    setDisabled(!cardId);
  }, [cardId]);

  const presetValues = useSelector(selectGiftCardSelectableAmounts);
  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      amount: presetValues[0],
    },
  });

  const amount = methods.watch('amount');
  const clientName = useSelector(selectClientName);
  const walletLabel = useMemo(
    () => t('payment.giftCardWalletLabel', { clientName }),
    [clientName, t],
  );
  const afterWalletPay = () => {
    dispatch(fetchPaymentInstrumentsStartAsync());
    handleClose();
  };
  const paymentContext = useGiftCardPayment(
    amount * 1000,
    walletLabel,
    paymentInstrumentId,
    afterWalletPay,
  );

  return (
    <Box data-testid="GiftCardAddValue.Box">
      {loading && <Spinner />}

      <Typography gutterBottom>
        {t('addPurchaseGiftCard.purchaseCard.minMax', { minAmount, maxAmount })}
      </Typography>
      <Payment value={paymentContext} paymentInstrumentId={cardId}>
        <FormProvider {...methods}>
          <GiftCardAddValueForm
            disabled={disabled}
            setCardId={setCardId}
            submitLabel={submitLabel}
            paymentInstrumentId={paymentInstrumentId}
            cardId={cardId}
            handleClose={handleClose}
            cards={cards}
          />
        </FormProvider>
      </Payment>
    </Box>
  );
};

export default GiftCardAddValue;
